import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Joyride, STATUS, ACTIONS, EVENTS } from 'react-joyride';

/**
 * Finds the actual scroll container used by the PWA layout.
 */
const getScrollContainer = () => {
    return document.querySelector('.overflow-y-auto.scrollbar-hide') 
        || document.querySelector('[class*="overflow-y-auto"]')
        || null;
};

/**
 * Scrolls the target element into view within the PWA's nested scroll container.
 * Key rule: NEVER let content go behind the header.
 */
const scrollTargetIntoView = (target, scrollContainer) => {
    if (!target || target === 'body') return;

    const element = typeof target === 'string' ? document.querySelector(target) : target;
    if (!element || !scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    // Is the element already fully visible?
    const topOk = elementRect.top >= containerRect.top;
    const bottomOk = elementRect.bottom <= containerRect.bottom - 120; // 120px buffer for tooltip + bottom nav

    if (topOk && bottomOk) return; // Already visible, return current position

    // Calculate where the element sits inside the scrollable content
    const elementOffsetInContent = elementRect.top - containerRect.top + scrollContainer.scrollTop;

    // Place element in the upper-middle of the container (1/3 from the top)
    // This gives plenty of room above it for a tooltip with placement: 'top'
    const idealScrollTop = elementOffsetInContent - (containerRect.height / 3);
    return Math.max(0, idealScrollTop);
};

// Compact mobile tooltip — tap-to-advance, no back/next buttons
const CustomTooltip = ({
    index,
    step,
    primaryProps,
    skipProps,
    tooltipProps,
    size,
    isLastStep,
}) => {
    return (
        <div
            {...tooltipProps}
            onClick={(e) => {
                // Advance the tour if clicking anywhere on the modal except buttons
                if (e.target.closest('button')) return;
                if (!isLastStep && primaryProps?.onClick) {
                    primaryProps.onClick(e);
                }
            }}
            style={{
                backgroundColor: '#ffffff',
                borderRadius: '10px',
                padding: 0,
                maxWidth: '280px',
                width: 'calc(100vw - 48px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                overflow: 'hidden',
                fontFamily: "'Inter', sans-serif",
                border: '1px solid #e5e7eb',
                cursor: !isLastStep ? 'pointer' : 'default',
            }}
        >
            <div style={{ position: 'relative', zIndex: 2 }}>
            {/* Header row: step counter + skip/close */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px 0',
            }}>
                <span style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    color: '#166c98',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                }}>
                    {index + 1}/{size}
                </span>
                {!isLastStep && (
                    <button
                        {...skipProps}
                        onClick={(e) => {
                            if (skipProps && skipProps.onClick) skipProps.onClick(e);
                            window.dispatchEvent(new Event('close-sina-tour'));
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#9ca3af',
                            fontSize: '11px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            padding: '2px 0',
                            fontFamily: "'Inter', sans-serif",
                        }}
                    >
                        Skip
                    </button>
                )}
            </div>

            {/* Title */}
            {step.title && (
                <div style={{
                    padding: '6px 14px 0',
                    fontSize: '13px',
                    fontWeight: 700,
                    color: '#141824',
                    lineHeight: 1.3,
                }}>
                    {step.title}
                </div>
            )}

            {/* Content */}
            <div style={{
                padding: '4px 14px 10px',
                fontSize: '11.5px',
                lineHeight: 1.5,
                color: '#6b7280',
            }}>
                {step.content}
            </div>

            {/* Footer — only visible on last step */}
            {isLastStep ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: '6px 14px 10px',
                    borderTop: '1px solid #f3f4f6',
                    gap: '8px',
                }}>
                    <button
                        onClick={() => {
                            // Dispatch a custom event to restart the tour
                            window.dispatchEvent(new Event('restart-sina-tour'));
                        }}
                        style={{
                            background: 'none',
                            border: '1px solid #e5e7eb',
                            color: '#6b7280',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            fontFamily: "'Inter', sans-serif",
                        }}
                    >
                        Restart
                    </button>
                    <button
                        onClick={() => {
                            window.dispatchEvent(new Event('close-sina-tour'));
                        }}
                        style={{
                            background: '#166c98',
                            border: 'none',
                            color: '#ffffff',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            fontFamily: "'Inter', sans-serif",
                            boxShadow: '0 1px 3px rgba(22,108,152,0.3)',
                        }}
                    >
                        Done
                    </button>
                </div>
            ) : (
                /* Tap hint for non-last steps */
                <div style={{
                    padding: '0 14px 8px',
                    fontSize: '10px',
                    color: '#b0b5bd',
                    textAlign: 'right',
                    fontStyle: 'italic',
                }}>
                    Tap to continue →
                </div>
            )}
            </div>
        </div>
    );
};

export const BaseTour = ({ steps, storageKey, delay = 1200, onCallback, isCompleted, onTourComplete, skipPrompt = false }) => {
    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [showPrompt, setShowPrompt] = useState(false);

    useEffect(() => {
        if (!storageKey) return;
        
        // If API says tour is completed, don't show prompt
        if (isCompleted) {
            return;
        }

        // If user dismissed it this session, don't show
        if (sessionStorage.getItem(`hide_tour_session_${storageKey}`)) {
            return;
        }

        const timer = setTimeout(() => {
            if (skipPrompt) {
                setRun(true);
            } else {
                setShowPrompt(true);
            }
        }, delay);
        return () => clearTimeout(timer);
    }, [storageKey, delay, isCompleted, skipPrompt]);

    // SCROLL LOCK: Completely freeze the scroll container with overflow:hidden.
    // This prevents ANY visual flicker — nothing can scroll the container except us.
    const lockedScrollTopRef = React.useRef(0);
    const scrollContainerRef = React.useRef(null);

    useEffect(() => {
        if (!run && !showPrompt) return;

        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';

        const scrollContainer = getScrollContainer();
        scrollContainerRef.current = scrollContainer;
        if (!scrollContainer) return;

        // If the tour is actually starting (not just the prompt), reset scroll
        if (run && !showPrompt) {
            scrollContainer.scrollTop = 0;
            lockedScrollTopRef.current = 0;
        }

        // Lock the container: set overflow to hidden so nothing can scroll it
        const originalOverflow = scrollContainer.style.overflow;
        scrollContainer.style.overflow = 'hidden';

        return () => {
            // Restore original overflow
            scrollContainer.style.overflow = originalOverflow;
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
        };
    }, [run, showPrompt]);

    // Auto-close tour if a modal is opened
    useEffect(() => {
        if (!run) return;

        const checkAndCloseForModals = () => {
            const modal = document.querySelector('.modal-overlay');
            if (modal) setRun(false);
        };

        checkAndCloseForModals();

        const observer = new MutationObserver(() => checkAndCloseForModals());
        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [run, storageKey]);

    // Global tap interceptor removed as clicking outside should now hide the tour for the session

    // Listen for custom restart and close events
    useEffect(() => {
        const handleRestart = () => {
            setStepIndex(0);
            setRun(true);
        };
        const handleClose = () => {
            setRun(false);
            if (onTourComplete) onTourComplete();
        };
        const handleAdvance = () => {
            setStepIndex(prev => {
                if (prev >= steps.length - 1) {
                    setRun(false);
                    if (onTourComplete) onTourComplete();
                    return prev;
                }
                return prev + 1;
            });
        };
        window.addEventListener('restart-sina-tour', handleRestart);
        window.addEventListener('close-sina-tour', handleClose);
        window.addEventListener('advance-sina-tour', handleAdvance);
        return () => {
            window.removeEventListener('restart-sina-tour', handleRestart);
            window.removeEventListener('close-sina-tour', handleClose);
            window.removeEventListener('advance-sina-tour', handleAdvance);
        };
    }, [onTourComplete, steps.length]);

    // Global tap interceptor for overlay clicks
    useEffect(() => {
        if (!run) return;

        const handleGlobalClick = (e) => {
            // If they clicked the modal prompt (before tour runs), ignore
            if (document.querySelector('.fixed.inset-0.z-\\[100001\\]')?.contains(e.target)) return;
            
            const tooltip = document.querySelector('.__floater__body') || document.querySelector('[class*="joyride-tooltip"]');
            
            // If click was inside tooltip, let CustomTooltip's onClick handle it
            if (tooltip && tooltip.contains(e.target)) return;

            // If click was on a skip or done button somewhere else
            if (e.target.closest('button')) return;

            // Otherwise, they clicked the overlay, advance!
            window.dispatchEvent(new Event('advance-sina-tour'));
        };

        document.addEventListener('click', handleGlobalClick, true);
        return () => document.removeEventListener('click', handleGlobalClick, true);
    }, [run]);

    const handleJoyrideCallback = useCallback((data) => {
        const { status, action, type, step, index } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        // Before each step: briefly unlock container, set scroll position, re-lock
        if (type === EVENTS.STEP_BEFORE) {
            const target = step?.target;
            const scrollContainer = scrollContainerRef.current || getScrollContainer();
            if (target && target !== 'body' && scrollContainer) {
                const newPos = scrollTargetIntoView(target, scrollContainer);
                if (newPos !== undefined) {
                    // Briefly unlock, scroll, re-lock (synchronous — no visual flicker)
                    scrollContainer.style.overflow = 'auto';
                    scrollContainer.scrollTop = newPos;
                    lockedScrollTopRef.current = newPos;
                    scrollContainer.style.overflow = 'hidden';
                }
            } else if (target === 'body') {
                if (scrollContainer) {
                    scrollContainer.style.overflow = 'auto';
                    scrollContainer.scrollTop = 0;
                    lockedScrollTopRef.current = 0;
                    scrollContainer.style.overflow = 'hidden';
                }
            }
        }

        if (type === EVENTS.TARGET_NOT_FOUND) {
            // If a target element isn't found (e.g., list is empty), skip to the next step
            // instead of hanging on a grey screen
            if (index === steps.length - 1) {
                setRun(false);
                if (onTourComplete) onTourComplete();
            } else {
                setStepIndex(index + 1);
            }
            return;
        }

        if (type === EVENTS.STEP_AFTER) {
            // Update controlled step index
            // User requested that tapping anywhere (ACTIONS.CLOSE from overlay) advances the tour
            if (action === ACTIONS.NEXT || action === ACTIONS.CLOSE) {
                if (index === steps.length - 1) {
                    setRun(false);
                    if (onTourComplete) onTourComplete();
                } else {
                    setStepIndex(index + 1);
                }
            } else if (action === ACTIONS.PREV) {
                setStepIndex(index - 1);
            } else if (action === ACTIONS.SKIP) {
                // Skip button explicitly clicked
                setRun(false);
                if (onTourComplete) onTourComplete();
            }
        }

        if (finishedStatuses.includes(status)) {
            setRun(false);
            if (onTourComplete) onTourComplete();
        }

        if (onCallback) onCallback(data);
    }, [storageKey, onCallback, onTourComplete, steps.length]);

    return (
        <>
        <Joyride
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            continuous
            showSkipButton
            showProgress={false}
            disableCloseOnEsc={false}
            disableScrolling
            disableScrollParentFix
            disableOverlayClose={true}
            spotlightClicks={false}
            callback={handleJoyrideCallback}
            tooltipComponent={CustomTooltip}
            floaterProps={{
                disableAnimation: false,
                styles: {
                    arrow: { length: 8, spread: 12 },
                },
            }}
            styles={{
                options: {
                    arrowColor: '#ffffff',
                    backgroundColor: '#ffffff',
                    overlayColor: 'rgba(0, 0, 0, 0.5)',
                    primaryColor: '#166c98',
                    textColor: '#141824',
                    zIndex: 100000,
                },
                spotlight: {
                    borderRadius: '12px',
                },
            }}
        />
        
        {/* Start Tour Modal Prompt - Styled to match CustomTooltip */}
        {showPrompt && createPortal(
            <div 
                className="fixed inset-0 z-[100001] flex items-center justify-center p-4 bg-black/50 animate-fade-in backdrop-blur-[2px]"
                onClick={(e) => {
                    // Close prompt if clicking outside the modal box
                    if (e.target === e.currentTarget) {
                        setShowPrompt(false);
                        if (storageKey) {
                            sessionStorage.setItem(`hide_tour_session_${storageKey}`, 'true');
                        }
                    }
                }}
            >
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '10px',
                    padding: 0,
                    maxWidth: '280px',
                    width: 'calc(100vw - 48px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                    overflow: 'hidden',
                    fontFamily: "'Inter', sans-serif",
                    border: '1px solid #e5e7eb',
                }}>
                    <div style={{ position: 'relative', zIndex: 2 }}>
                        {/* Header row: Just to give some top padding like tooltip */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 14px 0',
                        }}>
                        </div>

                        {/* Title */}
                        <div style={{
                            padding: '6px 14px 0',
                            fontSize: '13px',
                            fontWeight: 700,
                            color: '#141824',
                            lineHeight: 1.3,
                        }}>
                            👋 Welcome to Terriqon!
                        </div>

                        {/* Content */}
                        <div style={{
                            padding: '4px 14px 10px',
                            fontSize: '11.5px',
                            lineHeight: 1.5,
                            color: '#6b7280',
                        }}>
                            Would you like a quick tour to see how everything works? It only takes a minute to show you around!
                        </div>

                        {/* Footer */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            padding: '6px 14px 10px',
                            borderTop: '1px solid #f3f4f6',
                            gap: '8px',
                        }}>
                            <button
                                onClick={() => {
                                    setShowPrompt(false);
                                    if (onTourComplete) onTourComplete();
                                }}
                                style={{
                                    background: 'none',
                                    border: '1px solid #e5e7eb',
                                    color: '#6b7280',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    padding: '5px 12px',
                                    borderRadius: '6px',
                                    fontFamily: "'Inter', sans-serif",
                                }}
                            >
                                Skip
                            </button>
                            <button
                                onClick={() => {
                                    setShowPrompt(false);
                                    setRun(true);
                                }}
                                style={{
                                    background: '#166c98',
                                    border: 'none',
                                    color: '#ffffff',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    padding: '5px 12px',
                                    borderRadius: '6px',
                                    fontFamily: "'Inter', sans-serif",
                                    boxShadow: '0 1px 3px rgba(22,108,152,0.3)',
                                }}
                            >
                                Start Tour
                            </button>
                        </div>
                    </div>
                </div>
            </div>,
            document.body
        )}
        </>
    );
};
